/**
 * ExpenseForm State Management Hook
 *
 * Encapsulates all state logic for the expense form.
 * This makes the form easier to test and maintain.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import type {
  GroupMemberResponseDto,
  CreateExpenseDto,
  UpdateExpenseDto,
  ExpenseResponseDto,
  MemberType,
} from "@tabipla/shared";
import { useFormDraft } from "@/application/hooks/useFormDraft";
import { getDraft } from "@/infrastructure/storage";
import {
  type ExpenseFormDraft,
  getInitialDraft,
  memberKey,
  parseMemberKey,
  expenseToDraft,
  calculateTotalCustomAmount,
  filterEligibleMembers,
  draftHasUserContent,
} from "@/utils/expense-form";

interface UseExpenseFormStateProps {
  open: boolean;
  members: GroupMemberResponseDto[];
  currentUserId: string;
  sessionId: string;
  editingExpense?: ExpenseResponseDto | null;
}

export function useExpenseFormState({
  open,
  members,
  currentUserId,
  sessionId,
  editingExpense,
}: UseExpenseFormStateProps) {
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const initialDraftCheckedRef = useRef(false);
  const isEditMode = !!editingExpense;

  // Find current user in members to create default payer key
  const currentUserMember = useMemo(
    () => members.find((m) => m.memberId === currentUserId && m.memberType === "user"),
    [members, currentUserId]
  );
  const currentUserKey = currentUserMember
    ? memberKey(currentUserMember.memberId, currentUserMember.memberType)
    : "";

  const {
    data: draft,
    setData: setDraft,
    hasDraft,
    restoreDraft,
    clearDraft,
    saveStatus,
  } = useFormDraft<ExpenseFormDraft>({
    key: isEditMode ? `expense-edit-${editingExpense.id}` : `expense-${sessionId}`,
    initialData: isEditMode ? expenseToDraft(editingExpense) : getInitialDraft(currentUserKey),
    debounceMs: 500,
    enabled: open,
  });

  // Reset form when editingExpense changes
  useEffect(() => {
    if (open && editingExpense) {
      setDraft(expenseToDraft(editingExpense));
    }
  }, [open, editingExpense, setDraft]);

  // Map to quickly look up member info from composite key
  const memberMap = useMemo(() => {
    const map = new Map<string, GroupMemberResponseDto>();
    members.forEach((m) => map.set(memberKey(m.memberId, m.memberType), m));
    return map;
  }, [members]);

  // Convert selectedMemberKeys to Set for easier operations
  const selectedMembers = useMemo(
    () => new Set(draft.selectedMemberKeys),
    [draft.selectedMemberKeys]
  );

  // Check if draft has actual user-entered content (not just default members)
  const hasUserContent = draftHasUserContent(draft);

  // Show draft banner ONLY when dialog first opens with an existing draft
  useEffect(() => {
    if (!open) {
      // Reset when dialog closes
      initialDraftCheckedRef.current = false;
      setShowDraftBanner(false);
      return;
    }

    // Only check once per dialog open
    if (initialDraftCheckedRef.current) return;
    initialDraftCheckedRef.current = true;

    // Check storage for existing draft using infrastructure service
    const draftKey = isEditMode ? `expense-edit-${editingExpense?.id}` : `expense-${sessionId}`;
    const result = getDraft<ExpenseFormDraft>(draftKey);
    if (result.data !== null && draftHasUserContent(result.data)) {
      setShowDraftBanner(true);
    }
  }, [open, sessionId, isEditMode, editingExpense?.id]);

  // Initialize selected members with members who joined before or on the expense date (only for new expense)
  useEffect(() => {
    if (
      open &&
      members.length > 0 &&
      draft.selectedMemberKeys.length === 0 &&
      !hasDraft &&
      !isEditMode
    ) {
      const expenseDate = new Date(draft.date);
      const eligibleMembers = filterEligibleMembers(members, expenseDate);
      setDraft((prev) => ({
        ...prev,
        selectedMemberKeys: eligibleMembers.map((m) => memberKey(m.memberId, m.memberType)),
      }));
    }
  }, [open, members, draft.selectedMemberKeys.length, hasDraft, setDraft, isEditMode, draft.date]);

  // Initialize payer to current user if not set (for new expenses)
  useEffect(() => {
    if (
      open &&
      members.length > 0 &&
      !draft.payerKey &&
      currentUserKey &&
      !hasDraft &&
      !isEditMode
    ) {
      setDraft((prev) => ({
        ...prev,
        payerKey: currentUserKey,
      }));
    }
  }, [open, members.length, draft.payerKey, currentUserKey, hasDraft, setDraft, isEditMode]);

  // Calculated values
  const totalCustomAmount = calculateTotalCustomAmount(draft.customAmounts, selectedMembers);
  const parsedAmount = parseInt(draft.amount, 10) || 0;
  const isValidCustomSplit = !draft.splitEqually && totalCustomAmount === parsedAmount;
  const isValid =
    draft.amount && selectedMembers.size > 0 && (draft.splitEqually || isValidCustomSplit);

  // Handlers
  const handleRestoreDraft = () => {
    restoreDraft();
    setShowDraftBanner(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftBanner(false);
    // Initialize with all members and current user as payer
    setDraft({
      ...getInitialDraft(currentUserKey),
      selectedMemberKeys: members.map((m) => memberKey(m.memberId, m.memberType)),
    });
  };

  const toggleMember = (key: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(key)) {
      if (newSelected.size > 1) {
        newSelected.delete(key);
      }
    } else {
      newSelected.add(key);
    }
    setDraft((prev) => ({
      ...prev,
      selectedMemberKeys: Array.from(newSelected),
    }));
  };

  const handleCustomAmountChange = (key: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      customAmounts: { ...prev.customAmounts, [key]: value },
    }));
  };

  const selectAllMembers = () => {
    const expenseDate = new Date(draft.date);
    const eligibleMembers = filterEligibleMembers(members, expenseDate);
    setDraft((prev) => ({
      ...prev,
      selectedMemberKeys: eligibleMembers.map((m) => memberKey(m.memberId, m.memberType)),
    }));
  };

  const clearAllMembers = () => {
    setDraft((prev) => ({
      ...prev,
      selectedMemberKeys: [],
    }));
  };

  const buildSubmitData = (
    translationFn: (key: string) => string
  ): CreateExpenseDto | UpdateExpenseDto | null => {
    if (isNaN(parsedAmount) || parsedAmount <= 0) return null;

    let shares: { memberId: string; memberType: MemberType; amount: number }[];

    if (draft.splitEqually) {
      const selectedArray = Array.from(selectedMembers);
      const shareAmount = Math.floor(parsedAmount / selectedArray.length);
      const remainder = parsedAmount - shareAmount * selectedArray.length;

      shares = selectedArray.map((key, index) => {
        const member = memberMap.get(key)!;
        return {
          memberId: member.memberId,
          memberType: member.memberType,
          amount: shareAmount + (index < remainder ? 1 : 0),
        };
      });
    } else {
      shares = Array.from(selectedMembers).map((key) => {
        const member = memberMap.get(key)!;
        return {
          memberId: member.memberId,
          memberType: member.memberType,
          amount: parseInt(draft.customAmounts[key] || "0", 10),
        };
      });
    }

    // Use category name as default description if empty
    const description =
      draft.description.trim() || translationFn(`expenses.categories.${draft.category}`);

    // Parse payer key to get paidBy fields
    const payer = draft.payerKey ? parseMemberKey(draft.payerKey) : null;

    return {
      amount: parsedAmount,
      currency: draft.currency,
      description,
      category: draft.category,
      date: new Date(draft.date).toISOString(),
      shares,
      // Include paidBy if a payer is selected
      ...(payer && {
        paidById: payer.memberId,
        paidByType: payer.memberType,
      }),
    };
  };

  return {
    // State
    draft,
    setDraft,
    showDraftBanner,
    setShowDraftBanner,
    saveStatus,
    isEditMode,

    // Computed values
    memberMap,
    selectedMembers,
    hasUserContent,
    totalCustomAmount,
    parsedAmount,
    isValid,
    currentUserKey,

    // Handlers
    handleRestoreDraft,
    handleDiscardDraft,
    toggleMember,
    handleCustomAmountChange,
    selectAllMembers,
    clearAllMembers,
    buildSubmitData,
    clearDraft,
  };
}
